// Comprehensive echocardiographic measurement definitions
// Organized by structure and modality

export const MEASUREMENT_CATEGORIES = {
  LV: 'Left Ventricle',
  RV: 'Right Ventricle', 
  LA: 'Left Atrium',
  RA: 'Right Atrium',
  AO: 'Aorta',
  MV: 'Mitral Valve',
  AV: 'Aortic Valve',
  TV: 'Tricuspid Valve',
  PV: 'Pulmonary Valve',
  DOPPLER: 'Doppler',
  STRAIN: 'Strain'
};

export const MODALITIES = {
  TWO_D: '2D',
  M_MODE: 'M-Mode',
  DOPPLER: 'Doppler',
  COLOR: 'Color Doppler',
  TISSUE: 'Tissue Doppler',
  STRAIN: 'Strain'
};

export const MEASUREMENT_TOOLS = {
  LENGTH: 'length',
  AREA: 'area',
  ELLIPSE: 'ellipse',
  ANGLE: 'angle',
  VELOCITY: 'velocity',
  TRACE: 'trace',
  VTI: 'vti' // Velocity Time Integral
};

// Comprehensive measurement definitions
export const ECHO_MEASUREMENTS = {
  // Left Ventricle - 2D
  LV_2D: {
    category: MEASUREMENT_CATEGORIES.LV,
    modality: MODALITIES.TWO_D,
    measurements: [
      {
        id: 'lv_edd_2d',
        name: 'LV End-Diastolic Diameter',
        shortName: 'LVIDd',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { male: [4.2, 5.8], female: [3.9, 5.3] },
        view: 'PLAX',
        instructions: 'Measure at the level of the mitral valve tips in end-diastole'
      },
      {
        id: 'lv_esd_2d',
        name: 'LV End-Systolic Diameter',
        shortName: 'LVIDs',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { male: [2.5, 4.0], female: [2.2, 3.5] },
        view: 'PLAX',
        instructions: 'Measure at the level of the mitral valve tips in end-systole'
      },
      {
        id: 'ivs_d_2d',
        name: 'IVS Diastolic Thickness',
        shortName: 'IVSd',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { male: [0.6, 1.0], female: [0.6, 0.9] },
        view: 'PLAX',
        instructions: 'Measure perpendicular to the septum in end-diastole'
      },
      {
        id: 'pw_d_2d',
        name: 'Posterior Wall Diastolic Thickness',
        shortName: 'PWd',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { male: [0.6, 1.0], female: [0.6, 0.9] },
        view: 'PLAX',
        instructions: 'Measure perpendicular to the posterior wall in end-diastole'
      },
      {
        id: 'lv_edv_biplane',
        name: 'LV End-Diastolic Volume (Biplane)',
        shortName: 'LVEDV',
        tool: MEASUREMENT_TOOLS.TRACE,
        unit: 'ml',
        normalRange: { male: [67, 155], female: [56, 104] },
        view: 'A4C/A2C',
        instructions: 'Trace endocardial border in A4C and A2C views at end-diastole'
      },
      {
        id: 'lv_esv_biplane',
        name: 'LV End-Systolic Volume (Biplane)',
        shortName: 'LVESV',
        tool: MEASUREMENT_TOOLS.TRACE,
        unit: 'ml',
        normalRange: { male: [22, 58], female: [19, 49] },
        view: 'A4C/A2C',
        instructions: 'Trace endocardial border in A4C and A2C views at end-systole'
      }
    ]
  },

  // Left Ventricle - M-Mode
  LV_MMODE: {
    category: MEASUREMENT_CATEGORIES.LV,
    modality: MODALITIES.M_MODE,
    measurements: [
      {
        id: 'lv_edd_mm',
        name: 'LV End-Diastolic Diameter (M-Mode)',
        shortName: 'LVIDd',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { male: [4.2, 5.8], female: [3.9, 5.3] },
        view: 'PLAX M-Mode',
        instructions: 'Measure from leading edge to leading edge at QRS onset'
      },
      {
        id: 'lv_esd_mm',
        name: 'LV End-Systolic Diameter (M-Mode)',
        shortName: 'LVIDs',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { male: [2.5, 4.0], female: [2.2, 3.5] },
        view: 'PLAX M-Mode',
        instructions: 'Measure at smallest LV dimension'
      },
      {
        id: 'fs_mm',
        name: 'Fractional Shortening',
        shortName: 'FS',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: '%',
        normalRange: { all: [25, 45] },
        view: 'PLAX M-Mode',
        calculated: true,
        formula: '((LVIDd - LVIDs) / LVIDd) * 100'
      }
    ]
  },

  // Left Atrium - 2D
  LA_2D: {
    category: MEASUREMENT_CATEGORIES.LA,
    modality: MODALITIES.TWO_D,
    measurements: [
      {
        id: 'la_diameter_ap',
        name: 'LA Anteroposterior Diameter',
        shortName: 'LA AP',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { male: [3.0, 4.0], female: [2.7, 3.8] },
        view: 'PLAX',
        instructions: 'Measure from aortic root posterior wall to LA posterior wall'
      },
      {
        id: 'la_volume_biplane',
        name: 'LA Volume (Biplane)',
        shortName: 'LAV',
        tool: MEASUREMENT_TOOLS.TRACE,
        unit: 'ml',
        normalRange: { male: [18, 58], female: [22, 52] },
        view: 'A4C/A2C',
        instructions: 'Trace LA endocardial border excluding pulmonary veins and LAA'
      },
      {
        id: 'la_area_a4c',
        name: 'LA Area (A4C)',
        shortName: 'LA Area',
        tool: MEASUREMENT_TOOLS.AREA,
        unit: 'cm²',
        normalRange: { all: { max: 20 } },
        view: 'A4C',
        instructions: 'Trace LA endocardial border at end-systole'
      }
    ]
  },

  // Right Ventricle - 2D
  RV_2D: {
    category: MEASUREMENT_CATEGORIES.RV,
    modality: MODALITIES.TWO_D,
    measurements: [
      {
        id: 'rv_basal',
        name: 'RV Basal Diameter',
        shortName: 'RVD1',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { all: [2.5, 4.1] },
        view: 'A4C',
        instructions: 'Measure at basal 1/3 of RV in end-diastole'
      },
      {
        id: 'rv_mid',
        name: 'RV Mid Diameter',
        shortName: 'RVD2',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { all: [1.9, 3.5] },
        view: 'A4C',
        instructions: 'Measure at mid level of RV in end-diastole'
      },
      {
        id: 'rv_length',
        name: 'RV Length',
        shortName: 'RVD3',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { all: [5.5, 8.6] },
        view: 'A4C',
        instructions: 'Measure from tricuspid annulus to RV apex'
      },
      {
        id: 'tapse',
        name: 'TAPSE',
        shortName: 'TAPSE',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { all: { min: 1.7 } },
        view: 'A4C M-Mode',
        instructions: 'M-Mode through lateral tricuspid annulus, measure systolic excursion'
      }
    ]
  },

  // Right Atrium - 2D
  RA_2D: {
    category: MEASUREMENT_CATEGORIES.RA,
    modality: MODALITIES.TWO_D,
    measurements: [
      {
        id: 'ra_major',
        name: 'RA Major Dimension',
        shortName: 'RA Major',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { all: [3.5, 5.3] },
        view: 'A4C',
        instructions: 'Measure from mid tricuspid annulus to superior RA wall'
      },
      {
        id: 'ra_minor',
        name: 'RA Minor Dimension',
        shortName: 'RA Minor',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { all: [2.9, 4.5] },
        view: 'A4C',
        instructions: 'Measure perpendicular to major dimension at mid RA'
      },
      {
        id: 'ra_area',
        name: 'RA Area',
        shortName: 'RA Area',
        tool: MEASUREMENT_TOOLS.AREA,
        unit: 'cm²',
        normalRange: { male: { max: 25 }, female: { max: 20 } },
        view: 'A4C',
        instructions: 'Trace RA endocardial border at end-systole'
      }
    ]
  },

  // Aorta - 2D
  AO_2D: {
    category: MEASUREMENT_CATEGORIES.AO,
    modality: MODALITIES.TWO_D,
    measurements: [
      {
        id: 'ao_root',
        name: 'Aortic Root Diameter',
        shortName: 'Ao Root',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { male: [2.2, 3.6], female: [2.0, 3.2] },
        view: 'PLAX',
        instructions: 'Measure at sinus of Valsalva level in end-diastole'
      },
      {
        id: 'ao_stj',
        name: 'Sinotubular Junction',
        shortName: 'STJ',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { male: [2.1, 3.5], female: [1.9, 3.1] },
        view: 'PLAX',
        instructions: 'Measure at sinotubular junction in end-diastole'
      },
      {
        id: 'ao_ascending',
        name: 'Ascending Aorta',
        shortName: 'Asc Ao',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { all: { max: 3.7 } },
        view: 'PLAX',
        instructions: 'Measure 1cm above sinotubular junction'
      }
    ]
  },

  // Mitral Valve - Doppler
  MV_DOPPLER: {
    category: MEASUREMENT_CATEGORIES.MV,
    modality: MODALITIES.DOPPLER,
    measurements: [
      {
        id: 'mv_e_wave',
        name: 'MV E-wave Velocity',
        shortName: 'E',
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: 'cm/s',
        normalRange: { all: [60, 130] },
        view: 'A4C PW',
        instructions: 'PW at mitral valve tips, measure peak E velocity'
      },
      {
        id: 'mv_a_wave',
        name: 'MV A-wave Velocity',
        shortName: 'A',
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: 'cm/s',
        normalRange: { age_dependent: true },
        view: 'A4C PW',
        instructions: 'PW at mitral valve tips, measure peak A velocity'
      },
      {
        id: 'mv_e_a_ratio',
        name: 'E/A Ratio',
        shortName: 'E/A',
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: '',
        normalRange: { young: [1.0, 2.0], elderly: [0.8, 2.0] },
        view: 'A4C PW',
        calculated: true,
        formula: 'E / A'
      },
      {
        id: 'mv_dec_time',
        name: 'MV Deceleration Time',
        shortName: 'DT',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'ms',
        normalRange: { all: [160, 240] },
        view: 'A4C PW',
        instructions: 'Measure from peak E to baseline'
      },
      {
        id: 'mv_vti',
        name: 'MV VTI',
        shortName: 'MV VTI',
        tool: MEASUREMENT_TOOLS.VTI,
        unit: 'cm',
        normalRange: { all: [15, 25] },
        view: 'A4C PW',
        instructions: 'Trace mitral inflow velocity envelope'
      }
    ]
  },

  // Aortic Valve - Doppler
  AV_DOPPLER: {
    category: MEASUREMENT_CATEGORIES.AV,
    modality: MODALITIES.DOPPLER,
    measurements: [
      {
        id: 'av_peak_vel',
        name: 'AV Peak Velocity',
        shortName: 'AV Vmax',
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: 'm/s',
        normalRange: { all: { max: 2.0 } },
        view: 'A5C CW',
        instructions: 'CW through AV, measure peak velocity'
      },
      {
        id: 'av_mean_grad',
        name: 'AV Mean Gradient',
        shortName: 'AV MG',
        tool: MEASUREMENT_TOOLS.VTI,
        unit: 'mmHg',
        normalRange: { all: { max: 20 } },
        view: 'A5C CW',
        instructions: 'Trace AV velocity envelope'
      },
      {
        id: 'av_vti',
        name: 'AV VTI',
        shortName: 'AV VTI',
        tool: MEASUREMENT_TOOLS.VTI,
        unit: 'cm',
        normalRange: { all: [18, 25] },
        view: 'A5C CW',
        instructions: 'Trace aortic velocity envelope'
      },
      {
        id: 'lvot_diameter',
        name: 'LVOT Diameter',
        shortName: 'LVOT',
        tool: MEASUREMENT_TOOLS.LENGTH,
        unit: 'cm',
        normalRange: { male: [1.9, 2.5], female: [1.7, 2.3] },
        view: 'PLAX',
        instructions: 'Measure LVOT diameter in mid-systole'
      },
      {
        id: 'lvot_vti',
        name: 'LVOT VTI',
        shortName: 'LVOT VTI',
        tool: MEASUREMENT_TOOLS.VTI,
        unit: 'cm',
        normalRange: { all: [18, 22] },
        view: 'A5C PW',
        instructions: 'PW in LVOT, trace velocity envelope'
      }
    ]
  },

  // Tricuspid Valve - Doppler
  TV_DOPPLER: {
    category: MEASUREMENT_CATEGORIES.TV,
    modality: MODALITIES.DOPPLER,
    measurements: [
      {
        id: 'tr_peak_vel',
        name: 'TR Peak Velocity',
        shortName: 'TR Vmax',
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: 'm/s',
        normalRange: { all: { max: 2.8 } },
        view: 'A4C/PSAX CW',
        instructions: 'CW through TR jet, measure peak velocity'
      },
      {
        id: 'tr_peak_grad',
        name: 'TR Peak Gradient',
        shortName: 'TR PG',
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: 'mmHg',
        normalRange: { all: { max: 30 } },
        view: 'A4C/PSAX CW',
        calculated: true,
        formula: '4 * TR_Vmax²'
      },
      {
        id: 'rvsp',
        name: 'RVSP',
        shortName: 'RVSP',
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: 'mmHg',
        normalRange: { all: { max: 35 } },
        view: 'A4C/PSAX CW',
        calculated: true,
        formula: 'TR_PG + RAP',
        requiresInput: ['RAP']
      }
    ]
  },

  // Tissue Doppler
  TISSUE_DOPPLER: {
    category: MEASUREMENT_CATEGORIES.DOPPLER,
    modality: MODALITIES.TISSUE,
    measurements: [
      {
        id: 'septal_e_prime',
        name: "Septal e'",
        shortName: "Sep e'",
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: 'cm/s',
        normalRange: { all: { min: 7 } },
        view: 'A4C TDI',
        instructions: 'TDI at septal mitral annulus, measure early diastolic velocity'
      },
      {
        id: 'lateral_e_prime',
        name: "Lateral e'",
        shortName: "Lat e'",
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: 'cm/s',
        normalRange: { all: { min: 10 } },
        view: 'A4C TDI',
        instructions: 'TDI at lateral mitral annulus, measure early diastolic velocity'
      },
      {
        id: 'average_e_prime',
        name: "Average e'",
        shortName: "Avg e'",
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: 'cm/s',
        normalRange: { all: { min: 9 } },
        calculated: true,
        formula: "(Sep e' + Lat e') / 2"
      },
      {
        id: 'e_e_prime_ratio',
        name: "E/e' Ratio",
        shortName: "E/e'",
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: '',
        normalRange: { all: { max: 14 } },
        calculated: true,
        formula: "E / Avg e'"
      },
      {
        id: 'septal_s_prime',
        name: "Septal s'",
        shortName: "Sep s'",
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: 'cm/s',
        normalRange: { all: { min: 7 } },
        view: 'A4C TDI',
        instructions: 'TDI at septal mitral annulus, measure systolic velocity'
      },
      {
        id: 'lateral_s_prime',
        name: "Lateral s'",
        shortName: "Lat s'",
        tool: MEASUREMENT_TOOLS.VELOCITY,
        unit: 'cm/s',
        normalRange: { all: { min: 10 } },
        view: 'A4C TDI',
        instructions: 'TDI at lateral mitral annulus, measure systolic velocity'
      }
    ]
  },

  // Calculated Values
  CALCULATED: {
    category: 'Calculated',
    modality: 'Calculated',
    measurements: [
      {
        id: 'ef_simpson',
        name: 'Ejection Fraction (Simpson)',
        shortName: 'EF',
        unit: '%',
        normalRange: { male: [52, 72], female: [54, 74] },
        calculated: true,
        formula: '((LVEDV - LVESV) / LVEDV) * 100'
      },
      {
        id: 'stroke_volume',
        name: 'Stroke Volume',
        shortName: 'SV',
        unit: 'ml',
        normalRange: { all: [60, 100] },
        calculated: true,
        formula: 'LVEDV - LVESV'
      },
      {
        id: 'cardiac_output',
        name: 'Cardiac Output',
        shortName: 'CO',
        unit: 'L/min',
        normalRange: { all: [4, 8] },
        calculated: true,
        formula: '(SV * HR) / 1000',
        requiresInput: ['HR']
      },
      {
        id: 'cardiac_index',
        name: 'Cardiac Index',
        shortName: 'CI',
        unit: 'L/min/m²',
        normalRange: { all: [2.5, 4.0] },
        calculated: true,
        formula: 'CO / BSA',
        requiresInput: ['BSA']
      },
      {
        id: 'lv_mass',
        name: 'LV Mass (ASE)',
        shortName: 'LV Mass',
        unit: 'g',
        normalRange: { male: [88, 224], female: [67, 162] },
        calculated: true,
        formula: '0.8 * (1.04 * ((LVIDd + IVSd + PWd)³ - LVIDd³)) + 0.6'
      },
      {
        id: 'lv_mass_index',
        name: 'LV Mass Index',
        shortName: 'LVMI',
        unit: 'g/m²',
        normalRange: { male: [50, 102], female: [44, 88] },
        calculated: true,
        formula: 'LV_Mass / BSA',
        requiresInput: ['BSA']
      },
      {
        id: 'relative_wall_thickness',
        name: 'Relative Wall Thickness',
        shortName: 'RWT',
        unit: '',
        normalRange: { all: [0.32, 0.42] },
        calculated: true,
        formula: '(2 * PWd) / LVIDd'
      },
      {
        id: 'ava_continuity',
        name: 'AVA (Continuity)',
        shortName: 'AVA',
        unit: 'cm²',
        normalRange: { all: { min: 2.0 } },
        calculated: true,
        formula: '(π * (LVOT/2)²) * (LVOT_VTI / AV_VTI)'
      },
      {
        id: 'lavi',
        name: 'LA Volume Index',
        shortName: 'LAVI',
        unit: 'ml/m²',
        normalRange: { all: [16, 34] },
        calculated: true,
        formula: 'LAV / BSA',
        requiresInput: ['BSA']
      }
    ]
  }
};

// Helper function to get measurements by category
export function getMeasurementsByCategory(category) {
  return Object.values(ECHO_MEASUREMENTS)
    .filter(group => group.category === category)
    .flatMap(group => group.measurements);
}

// Helper function to get measurements by modality
export function getMeasurementsByModality(modality) {
  return Object.values(ECHO_MEASUREMENTS)
    .filter(group => group.modality === modality)
    .flatMap(group => group.measurements);
}

// Helper function to get all calculated measurements
export function getCalculatedMeasurements() {
  return Object.values(ECHO_MEASUREMENTS)
    .flatMap(group => group.measurements)
    .filter(m => m.calculated);
}

// Helper function to validate measurement value
export function validateMeasurement(measurementId, value, gender = 'all', age = null) {
  const measurement = Object.values(ECHO_MEASUREMENTS)
    .flatMap(group => group.measurements)
    .find(m => m.id === measurementId);
  
  if (!measurement || !measurement.normalRange) return null;
  
  const range = measurement.normalRange[gender] || measurement.normalRange.all;
  if (!range) return null;
  
  // Handle array format [min, max]
  if (Array.isArray(range)) {
    const [min, max] = range;
    if (value < min) return 'below';
    if (value > max) return 'above';
    return 'normal';
  }
  
  // Handle object format { min: x, max: y }
  if (typeof range === 'object') {
    if (range.min !== undefined && value < range.min) return 'below';
    if (range.max !== undefined && value > range.max) return 'above';
    return 'normal';
  }
  
  return null;
}

// Helper function to calculate derived measurements
export function calculateDerivedMeasurement(measurementId, values) {
  const measurement = getCalculatedMeasurements().find(m => m.id === measurementId);
  if (!measurement) return null;
  
  // Parse and evaluate formula
  let formula = measurement.formula;
  Object.entries(values).forEach(([key, value]) => {
    formula = formula.replace(new RegExp(key, 'g'), value);
  });
  
  try {
    // Use Function constructor for safe evaluation
    return new Function('return ' + formula)();
  } catch (error) {
    console.error('Error calculating measurement:', error);
    return null;
  }
}

export default ECHO_MEASUREMENTS;